import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Overline } from "@/components/Bits";
import { PORTRAIT, LOGO_CREST } from "@/lib/assets";

export function AuthShell({ children, heading, sub }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="auth-shell">
      <div className="relative hidden lg:block">
        <img src={PORTRAIT} alt="Apostle Blaise Judah" className="h-full w-full object-cover object-[center_20%]" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
        <div className="absolute bottom-0 left-0 p-14">
          <p className="font-accent italic text-4xl text-white leading-tight max-w-md">
            "Building Kingdom Leaders for Global Impact."
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.3em] text-gold-500">House of Blessing Ministries</p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-8 md:px-16 lg:px-20 py-16 relative">
        <Link to="/" className="absolute top-8 left-8 md:left-16 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-500 hover:text-gold-300 transition-colors">
          <ArrowLeft size={14} /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md mx-auto">
          <img src={LOGO_CREST} alt="Crest" className="h-14 w-14 object-contain [mix-blend-mode:screen] mb-6" />
          <Overline>Judah AI Academy</Overline>
          <h1 className="mt-5 font-serif text-4xl md:text-5xl text-white">{heading}</h1>
          <p className="mt-3 text-white/60 text-sm">{sub}</p>
          {children}
        </motion.div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(form.email, form.password);
      toast.success("Welcome back.");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <AuthShell heading="Welcome back" sub="Sign in to continue your learning journey.">
      <form onSubmit={submit} className="mt-10 space-y-4" data-testid="login-form">
        <Input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" className="bg-ink-paper border-gold-500/25 text-white placeholder:text-white/40 py-6" />
        <Input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" className="bg-ink-paper border-gold-500/25 text-white placeholder:text-white/40 py-6" />
        <div className="flex justify-end -mt-2">
          <Link to="/forgot-password" data-testid="to-forgot-password" className="text-xs uppercase tracking-[0.15em] text-gold-400 hover:text-gold-300">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold py-6">
          {busy ? "Signing in…" : "Sign In"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-white/60">
        New to the Academy? <Link to="/register" className="text-gold-400 hover:underline" data-testid="to-register">Create an account</Link>
      </p>
    </AuthShell>
  );
}
